/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { type AxiosInstance, type AxiosResponse } from 'axios';
import { createQuery } from '@/utils/createReactQuery';
import {
  type TrackedOrderData,
  type TrackedOrderOptions,
  type TrackedOrderResponse,
} from '../interface';
import { requestBaseURL } from '@/config/serviceAPI';

const URL = `${requestBaseURL}/ecomm/b2c/v1/order/lookup`;

const getTrackedOrder = async (options: TrackedOrderOptions, axiosInstance?: AxiosInstance) => {
  const { orderId, email } = options;

  if (!orderId || !email) {
    throw new Error('orderId and email are required');
  }

  const urlPath = `${URL}?email_id=${encodeURIComponent(email)}&order_id=${orderId}`;

  const response = await getAxios(axiosInstance).get<TrackedOrderResponse>(urlPath);

  return selector(response);
};

export const selector = (response: AxiosResponse<TrackedOrderResponse>): TrackedOrderData =>
  response.data;

const { useData: useTrackedOrderData, query: trackedOrderQuery } = createQuery<
  TrackedOrderData,
  TrackedOrderOptions
>('trackedOrder', getTrackedOrder);

const useTrackedOrder = ({ orderId, email, enabled }: TrackedOrderOptions) => {
  return useTrackedOrderData({
    orderId,
    email,
    enabled: !!orderId && !!email && enabled,
  });
};

export { useTrackedOrder, trackedOrderQuery };
